import _ from "lodash";
import React, { useRef, useMemo, useEffect, useState } from "react";
import { useAIChatContext } from "UIComponents/AIChat/context/AIChatContext";
import GenerateTranscriptsButton from "AppComponents/AIComponents/GenerateTranscriptsButton";
import moment from "moment";

// Third party
import classNames from "classnames";
import { useI18n } from "Hooks";
import { connect } from "react-redux";

// Utils
import {
  getAIText,
  getBlocks,
  getUserBlockActions,
  getAssistantBlockActions,
  getTextForClipboard,
  parseFieldViewContent,
  parseListForTextList,
  escapeMarkdownInUserMessage,
  parseListViewContentToTextListData,
} from "./modules/Utils";
import {
  getStyleStrippedText,
  checkScrollAtBottom,
  scrollIntoView,
} from "Utils";
import {
  extractAttachmentIdsFromValue,
  getCustomType,
  isModalityAttachmentType,
  getIsAIResponseLoading,
} from "../../modules/Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { copyToClipboardWithMimeType } from "Utils/CopyToClipboard";
import { getUnprocessedLinkAttachments } from "Utils/AIUtils";

// Modules
import { openLinkInNewTab, updateAIConversation } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { deleteAiMessages } from "../../modules/AIChatModules";

// Design system
import {
  Avatar,
  Tooltip,
  TextList,
  Button,
  MediaCard,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  DownArrowOutlined,
  ExpandArrowsTwoOutlined,
  InformationOutlined,
  RefreshArrowOutlined,
  VideoOutlined,
} from "@toddle-edu/ds-icons";

// Constants
import {
  WRITING,
  ANALYSING,
  GENERATING,
  THINKING,
  SEARCHING,
  BROWSING_THE_INTERNET,
  GENERATING_EVALUATION,
  FETCHING_TRANSCRIPT,
  CREATING_BANNER,
  CREATING_BANNER_WITHOUT_TOKEN,
} from "Constants/stringConstants";

import { AI_TUTOR_LEARNING_TOOLS } from "@toddle-edu/common-utils";

// Components
import MarkdownView from "UIComponents/MarkdownView";
import TheatreModeV2 from "UIComponents/TheatreModeV2";
import SeeMoreLessHtml from "UIComponents/SeeMoreLessHtml";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import CircleLoading from "UIComponents/CircleLoading";
import ProgressIndicator from "../Body/components/ProgressIndicator";
import BannerImageBlock from "./components/BannerImageBlock";

// SCSS
import classes from "./Body.scss";

const SCROLL_END_HEIGHT = 56;
const CONTAINER_PADDING = 20;
const WINDOW_WIDTH_BREAKPOINT = 708;
const TWO = 2;
const THREE = 3;

const Anchor = ({ href, children }) => {
  const { videoPlayerRef } = useAIChatContext();

  const isTimestampLink = /^#ts-\d+$/.test(href);

  const onClick = e => {
    if (isTimestampLink) {
      e.preventDefault();

      const match = href.match(/^#ts-(\d+)$/);
      const index = match ? parseInt(match[1], 10) - 1 : null;

      if (
        !_.isNil(index) &&
        videoPlayerRef?.current?.showCheckpointPopoverByIndex
      ) {
        videoPlayerRef.current.showCheckpointPopoverByIndex(index);
      }
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.anchor}
      onClick={onClick}
    >
      {_.first(children)}
    </a>
  );
};

const markdownComponents = {
  hr: () => <p>{"-----"}</p>,
  a: Anchor,
  h1: ({ children }) => <h1 className="text-heading-6">{children}</h1>,
  h2: ({ children }) => <h2 className={"text-label-l"}>{children}</h2>,
  h3: ({ children }) => <h3 className={"text-label"}>{children}</h3>,
  p: ({ children }) => <p className={"text-body"}>{children}</p>,
  strong: ({ children }) => <span className={"text-label"}>{children}</span>,
  b: ({ children }) => <span className={"text-label"}>{children}</span>,
  blockquote: ({ children }) => children,
};

const styles = {
  containerStyle: {
    padding: "0",
    backgroundColor: "var(--surface-primary-enabled)",
  },
  aiFeedbackStripStyle: {
    containerStyle: {
      padding: "0",
      backgroundColor: "unset",
    },
    iconContainerStyle: {
      gap: "8px",
    },
  },
  overlayInnerStyle: {
    maxWidth: "240px",
  },
  playbackSpeedButtonProps: {
    type: "plain",
  },
  errorAlert: {
    color: "red",
    border: "1px solid var(--decorative-background-red)",
  },
  retryIcon: {
    color: "var(--decorative-background-subtle-neutral)",
    border: "none",
  },
};

const Body = props => {
  const {
    sourceFrom,
    setToastMsg,
    openLinkInNewTab,
    customContentParser,
    onClickAction,
    customAIActionsList,
    customUserActionsList,
    renderCustomMessage,
    blockCustomClasses,
    showFeedbackStrip = true,
    showLikeDislikeButton = true,
    blockContentCustomClasses,
    renderEmptyState = _.noop,
    checkAssistantMessageVisibility,
    customAssistantMessageRenderer,
    customLoadingTextRenderer,
    customAssistantTrailingMessageRenderer,
    deleteAiMessages,
    updateAIConversation,
  } = props;

  const {
    mode,
    setValue,
    conversation,
    currentAIState,
    setCurrentAIState,
    setLocalAttachments,
    lastUserMessageRef,
    showScrollToBottom,
    setShowScrollToBottom,
    shouldScrollToBottom,
    isScrollingToBottom,
    setIsScrollingToBottom,
    shouldRenderEmptySpace,
    setShouldScrollToBottom,
    selectedModalityId,
    onMediaClick: contextOnMediaClick,
    setIsAIGeneratingResponse,
    onSendMessage = _.noop,
    messageAttachments,
    localAttachments,
    entitySubType,
    isAIGeneratingResponse,
    setSelectedLearningTool,
    setEditMessageId,
    announcementId,
  } = useAIChatContext();

  const conversationMetadata = _.get(conversation, "metadata", {});

  const savedStudentAudioSpeed = _.get(
    conversationMetadata,
    "studentAudioSpeed",
    "1"
  );

  const messages = [..._.get(conversation, "messages.edges", [])].reverse();

  const { t } = useI18n();

  // ======= state =======
  const [mediaViewerData, setMediaViewerModalState] = useState(null);
  const [
    showOnlySingleColumnPrompts,
    setShowOnlySingleColumnPrompts,
  ] = useState(false);

  // ======== Refs ========
  const scrollRef = useRef(null);
  const scrollEndRef = useRef(null);
  const emptySpaceRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const hasInitializedMessages = useRef(false);

  // ======== effects ========
  useEffect(() => {
    if (!_.isEmpty(messages)) {
      scrollToBottom({ behaviour: "instant" });

      // scroll to bottom on component mount
      // and then stop scrolling to bottom
      setShouldScrollToBottom(false);
    }
  }, []);

  useEffect(() => {
    if (currentAIState === FETCHING_TRANSCRIPT) {
      setIsAIGeneratingResponse(true);
    }
  }, [currentAIState, setIsAIGeneratingResponse]);

  const shouldShowOnlySingleColumnPrompts = () => {
    const windowHeight = window.innerHeight;
    return typeof windowHeight === "number"
      ? windowHeight < WINDOW_WIDTH_BREAKPOINT
      : false;
  };

  // Check if empty state has scroll to determine prompt count
  useEffect(() => {
    const debouncedOnResize = _.debounce(() => {
      setShowOnlySingleColumnPrompts(shouldShowOnlySingleColumnPrompts());
    }, 100);
    debouncedOnResize();
    window.addEventListener("resize", debouncedOnResize);
    return () => {
      window.removeEventListener("resize", debouncedOnResize);
      debouncedOnResize.cancel && debouncedOnResize.cancel();
    };
  }, []);

  const combinedAttachments = _.uniqBy(
    [...(messageAttachments || []), ...(localAttachments || [])],
    "id"
  );

  const attachment = _.last(combinedAttachments);

  // Auto-trigger media click for opening message
  useEffect(() => {
    if (
      !_.isEmpty(attachment) &&
      contextOnMediaClick &&
      isModalityAttachmentType(attachment) &&
      entitySubType !== "ai_tutor_chat"
    ) {
      contextOnMediaClick({ attachment });
    }
  }, []);

  // Auto-trigger media click for opening message on student end
  useEffect(() => {
    if (hasInitializedMessages.current) {
      return;
    }

    const openingMsgAttachment = _.first(
      _.find(messages, ({ node }) => node?.metadata?.isOpeningMessage)?.node
        ?.attachments
    );

    if (
      !_.isEmpty(openingMsgAttachment) &&
      contextOnMediaClick &&
      isModalityAttachmentType(openingMsgAttachment) &&
      entitySubType === "ai_tutor_chat"
    ) {
      contextOnMediaClick({ attachment: openingMsgAttachment });
      hasInitializedMessages.current = true;
    }
  }, [attachment, contextOnMediaClick, entitySubType]);

  const getEmptySpaceHeight = () => {
    if (!messagesContainerRef.current || !lastUserMessageRef.current)
      return "0px";
    const children = Array.from(messagesContainerRef.current.children);
    const lastUserIndex = children.indexOf(lastUserMessageRef.current);

    let accumulatedHeight = lastUserMessageRef.current.getBoundingClientRect()
      .height;

    // iterate from the last user message to the end of the container
    for (let i = lastUserIndex + 1; i < children.length; i++) {
      accumulatedHeight += children[i].getBoundingClientRect().height;
    }

    accumulatedHeight += SCROLL_END_HEIGHT + CONTAINER_PADDING;

    // extra space so that message doesn't stick to the very top
    accumulatedHeight += 8;

    return `calc(100% - ${accumulatedHeight}px)`;
  };

  const lastUserMessageId = useMemo(() => {
    const lastUserMessage = _.findLast(messages, ({ node }) => {
      return node?.authorType === "user" && !node?.metadata?.isMeta;
    });

    return _.get(lastUserMessage, "node.id", "");
  }, [messages]);

  useEffect(() => {
    const emptySpaceElement = emptySpaceRef.current;
    if (emptySpaceElement) {
      emptySpaceElement.style.minHeight = getEmptySpaceHeight();
    }
  }, [messages, lastUserMessageId, currentAIState]);

  // after new messages, the chat may not be at the bottom
  // so we need to check if the scroll is at the bottom
  const throttledCheckScrollAtBottom = useMemo(() => {
    return _.throttle(() => {
      const element = scrollRef.current;
      if (element) {
        const isAtBottom = checkScrollAtBottom(element, 80);
        setShowScrollToBottom(!isAtBottom);
      }
    }, 500);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(messages)) {
      throttledCheckScrollAtBottom();
    }
  }, [messages]);

  // ======== variables ========

  // ======== handlers ========

  const updateStudentConversationAudioSpeed = speed => {
    const oldMetadata = _.get(conversation, "metadata", {});

    if (oldMetadata.studentAudioSpeed === speed) return;

    updateAIConversation({
      input: {
        id: conversation.id,
        metadata: {
          ...oldMetadata,
          studentAudioSpeed: speed,
        },
      },
    });
  };

  const onScrollBodyContainer = e => {
    const isAtBottom = checkScrollAtBottom(e.target, 80);

    if (!_.isEmpty(messages)) {
      setShowScrollToBottom(!isAtBottom);
    }

    if (isAtBottom) {
      setIsScrollingToBottom(false);
    }
  };

  const scrollToBottom = ({ isForced = false, behaviour = "smooth" }) => {
    const shouldScroll = isForced || shouldScrollToBottom;

    if (scrollEndRef.current && shouldScroll) {
      scrollIntoView({ element: scrollEndRef.current, behavior: behaviour });
      setIsScrollingToBottom(true);
    }
  };

  const handleActions = async params => {
    const { key, node } = params;
    const { content, id: messageId, attachments } = node || {};

    switch (key) {
      case "copy": {
        const parsedContent = _.isFunction(customContentParser)
          ? customContentParser(content)
          : content;

        const { plainText, htmlText } = getTextForClipboard({
          content: parsedContent,
        });

        copyToClipboardWithMimeType(
          {
            text: plainText,
            html: htmlText,
          },
          () => {
            setToastMsg({
              msg: "toastMsgs:copied_capital",
              type: "success",
            });
          }
        );
        break;
      }
      case "edit": {
        const editedMessage = _.find(
          messages,
          message => _.get(message, "node.id") === messageId
        );

        if (editedMessage) {
          // update the states
          setValue(_.trim(content) || "");
          if (!_.isEmpty(attachments)) setLocalAttachments(attachments);
        }
        break;
      }
      case "internalEdit": {
        const internalEditedMessage = _.find(
          messages,
          message => _.get(message, "node.id") === messageId
        );

        if (internalEditedMessage) {
          // update the states
          setValue(_.trim(content) || "");
          if (!_.isEmpty(attachments)) setLocalAttachments(attachments);
          setEditMessageId(messageId);
        }
        break;
      }
      case "delete": {
        await deleteAiMessages({
          messageId,
          conversationId: conversation?.id,
        });
      }
    }
  };

  // ======== components ========
  const renderMessageLoader = () => {
    if (currentAIState === FETCHING_TRANSCRIPT) {
      const {
        lastUserMessage,
        unprocessedLinks,
      } = getUnprocessedLinkAttachments(messages);

      if (!_.isEmpty(unprocessedLinks))
        return (
          <ProgressIndicator
            userMessage={lastUserMessage}
            unprocessedLinks={unprocessedLinks}
            onSendMessage={onSendMessage}
          />
        );
      else return null;
    }

    if (currentAIState === CREATING_BANNER_WITHOUT_TOKEN) {
      return (
        <SkeletonLoader
          width={"calc(100% - 36px)"}
          height="auto"
          customClasses={classes.skeletonLoader}
        />
      );
    }

    const isCurrentMessagePending = _.startsWith(
      _.last(messages)?.node?.id,
      "NEW_"
    );

    return _.includes(
      [
        WRITING,
        SEARCHING,
        ANALYSING,
        GENERATING,
        THINKING,
        BROWSING_THE_INTERNET,
        GENERATING_EVALUATION,
      ],
      currentAIState
    ) || customLoadingTextRenderer?.(currentAIState) ? (
      <div
        className={classNames(classes.assistantMessage, {
          [classes.loaderSpacing]: isCurrentMessagePending,
        })}
      >
        <div className={classes.generatingMsg}>
          <div className={classes.dot} />
          <div className={classes.generatingSingleMsg}>
            {getAIText({ t, currentAIState, customLoadingTextRenderer })}
          </div>
        </div>
      </div>
    ) : null;
  };

  const renderMediaCard = (node, attachments) => {
    if (_.isEmpty(attachments)) return null;

    return _.map(attachments, (attachment, index) => {
      const { id, name, url, type, mimeType, metadata } = attachment || {};
      const { id: nodeId } = node || {};
      const { version } = metadata || {};

      const onMediaClick = () => {
        // When clicked on modalities attachment do not open theatre mode
        if (contextOnMediaClick && isModalityAttachmentType(attachment)) {
          contextOnMediaClick({ attachment });
          return;
        }

        // skip theatre mode for link
        if (type === "LINK" || type === "VIDEO") {
          openLinkInNewTab({ url });
          return;
        }

        setMediaViewerModalState({
          attachments,
          activeAttachmentIndex: index,
        });
      };

      if (type === "AUDIO") {
        const messageAudioTranscripts = _.get(
          node,
          "metadata.messageAudioTranscripts",
          []
        );
        const audioTranscriptText = _.join(messageAudioTranscripts, " ");

        const buttonElement = (
          <GenerateTranscriptsButton
            id={id}
            audioTranscriptText={customContentParser(audioTranscriptText)}
            isStudent={true}
            isChatBody={true}
          />
        );
        return (
          <div
            id={`${nodeId}_AI_MESSAGE`}
            key={url}
            className={classes.audioAttachment}
          >
            <CompactAudioWaveformPlayer
              attachment={attachment}
              defaultPlaybackSpeed={savedStudentAudioSpeed}
              onAudioSpeedChange={updateStudentConversationAudioSpeed}
              buttonElement={
                sourceFrom !== "STUDENT_ASSIGNMENT_SUBMISSION"
                  ? buttonElement
                  : null
              }
              showDownload={false}
              isAppearanceLikeMediaCard={true}
              playbackSpeedButtonProps={styles.playbackSpeedButtonProps}
              activePlaybackSpeedTriggerClass={
                classes.activePlaybackSpeedTrigger
              }
            />

            {sourceFrom === "STUDENT_ASSIGNMENT_SUBMISSION" &&
            !_.isEmpty(audioTranscriptText) ? (
              <div
                className={classNames(
                  "text-body-s",
                  classes.audioTranscription
                )}
              >
                <SeeMoreLessHtml
                  truncateLength={500}
                  fullSource={customContentParser(audioTranscriptText)}
                  containerStyle={{ color: "var(--text-secondary" }}
                  seeMoreLessTextStyle={{ fontSizes: "12px" }}
                  seeLessText={t("common:view_less")}
                  seeMoreText={t("common:view_more")}
                />
              </div>
            ) : null}
          </div>
        );
      } else {
        const isActivated = selectedModalityId === id;

        const customType = getCustomType(attachment);
        let displayType = customType;

        if (type === "WORKBOOK") {
          displayType = "Whiteboard";
        }
        const subtext = version
          ? `${displayType} • ${t("common:version")} ${version}`
          : displayType;
        return (
          <div id={`${nodeId}_AI_MESSAGE`} key={id}>
            <MediaCard
              dsVersion="2.0"
              key={id}
              name={name}
              url={url}
              thumbUrl={url}
              type={customType}
              mimeType={mimeType}
              onClick={onMediaClick}
              subtext={subtext}
              className={classes.mediaCard}
              isActivated={isActivated}
              showMediaPreview={type === "IMAGE" && attachments?.length === 1}
              options={
                sourceFrom === "LESSON_PLANNING_ASSISTANT" &&
                entitySubType === "ai_tutor" &&
                isModalityAttachmentType(attachment) &&
                !selectedModalityId ? (
                  <IconButton
                    icon={<ExpandArrowsTwoOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size="small"
                  />
                ) : null
              }
            />
          </div>
        );
      }
    });
  };

  const renderBlocks = ({ blocks, node, currentAIState, isLastMessage }) => {
    const blockContentClass = classNames({
      [classes.markdownView]: true,
      ...(blockContentCustomClasses?.(node) || {}),
    });

    return (
      <div className={classes.blockContainer}>
        {_.map(blocks, block => {
          const { type, content } = block;

          switch (type) {
            case "ListView": {
              const {
                listData,
                isOrdered,
              } = parseListViewContentToTextListData(content);

              return (
                <div className={classes.listViewContainer}>
                  <TextList
                    data={listData}
                    type={isOrdered ? "ordered" : "unordered"}
                  />
                </div>
              );
            }

            case "HTMLView": {
              const htmlContent = _.join(content, "");
              return (
                <div
                  className={classes.htmlView}
                  dangerouslySetInnerHTML={{
                    __html: getStyleStrippedText({
                      text: htmlContent,
                      type: "RICH_TEXT_EDITOR",
                    }),
                  }}
                />
              );
            }

            case "FieldView": {
              const { parsedContent, type: fieldType } = parseFieldViewContent(
                content
              );

              if (fieldType === "simple") {
                // Simple text - render as markdown
                const updatedContent = _.join(parsedContent, "\n");
                return (
                  <MarkdownView
                    content={updatedContent}
                    components={markdownComponents}
                    contentContainerClass={blockContentClass}
                    enableLatexSanitization={true}
                  />
                );
              } else {
                // List content - parse for TextList
                const { listItems, title } = parseListForTextList(
                  parsedContent,
                  fieldType === "nestedList"
                );

                return (
                  <>
                    {title ? (
                      <MarkdownView
                        content={title}
                        components={markdownComponents}
                        contentContainerClass={blockContentClass}
                        enableLatexSanitization={true}
                      />
                    ) : null}
                    <div className={classes.textListContainer}>
                      <TextList data={listItems} type="unordered" />
                    </div>
                  </>
                );
              }
            }

            case "ATTACHMENTS": {
              const { id, attachments } = node || {};

              if (_.isEmpty(attachments) && _.startsWith(id, "NEW_")) {
                const lastToolCall = _.findLast(messages, ({ node }) => {
                  const { authorType, content } = node || {};
                  return (
                    authorType === "tool" &&
                    _.includes(content, "attachmentGroupId")
                  );
                });

                const lastToolCallContent =
                  _.get(lastToolCall, "node.content") || null;

                let mediaCardInfo = null;

                if (!_.isEmpty(lastToolCallContent)) {
                  try {
                    const { response } = JSON.parse(lastToolCallContent);
                    mediaCardInfo = _.pick(response, ["name", "type"]);

                    if (mediaCardInfo.type === "TODDLE_DOC") {
                      mediaCardInfo.type = "TODDLE-DOC";
                    }
                  } catch {
                    mediaCardInfo = null;
                  }
                }

                return (
                  <div className={classes.blockAttachmentsContainer}>
                    {mediaCardInfo ? (
                      <MediaCard
                        {...mediaCardInfo}
                        dsVersion="2.0"
                        icon={<CircleLoading />}
                        disabled={true}
                        className={classes.mediaCard}
                      />
                    ) : (
                      <SkeletonLoader
                        width={288}
                        height={60}
                        customClasses={classes.attachmentSkeleton}
                      />
                    )}
                  </div>
                );
              }

              return (
                <div className={classes.blockAttachmentsContainer}>
                  {renderMediaCard(node, attachments)}
                </div>
              );
            }
            case "BannerSkeletonLoader":
            case "BannerImageView": {
              const _content = _.get(content, 0, "");
              const url = _content
                .match(/<banner_image>([\s\S]*?)<\/banner_image>/i)?.[1]
                ?.trim();
              const loadingBanner =
                isLastMessage && currentAIState === CREATING_BANNER;
              if (type === "BannerSkeletonLoader" && !loadingBanner)
                return null;

              return (
                <BannerImageBlock
                  node={node}
                  url={url}
                  conversation={conversation}
                  announcementId={announcementId}
                  loadingBanner={loadingBanner}
                  onImageClick={setMediaViewerModalState}
                />
              );
            }

            default: {
              // join lines
              let formattedContent = _.join(content, "\n");

              // remove unnecessary code blocks
              formattedContent = _.replace(formattedContent, /’/g, "'");

              // remove <br/> tags only if the content doesn't have table pattern
              const hasTablePattern = /^\s*\|.*\|$/m.test(formattedContent);
              if (!hasTablePattern) {
                formattedContent = _.replace(
                  formattedContent,
                  /<br\s*\/?>/g,
                  "\n"
                );
              }

              const blockContentClass = classNames({
                [classes.markdownView]: true,
                [classes.markdownViewV2]: hasTablePattern,
                ...(blockContentCustomClasses?.(node) || {}),
              });

              return (
                <MarkdownView
                  content={formattedContent}
                  components={markdownComponents}
                  contentContainerClass={blockContentClass}
                  enableLatexSanitization={true}
                  enableHTMLrendering={hasTablePattern}
                />
              );
            }
          }
        })}
      </div>
    );
  };

  const renderActions = ({ actionsList, node, onClickAction }) => {
    const _onClickAction = async params => {
      const { key } = params || {};

      // Deleting is handled internally, so always call handleActions.
      // Also fall back to handleActions when no external click handler is provided.
      if (key === "delete" || key === "internalEdit" || !onClickAction) {
        handleActions({ key, node });
        return;
      }

      onClickAction({
        key,
        node,
        updateContentValue: setValue,
        updateAttachmentValue: setLocalAttachments,
      });
    };

    return (
      <div className={classes.blockActions}>
        {_.map(actionsList, action => {
          const {
            key,
            label,
            icon,
            disabled = false,
            disabledTooltip,
            isLocalised = false,
            tooltipProps = {},
            tooltipLabel,
            localisationParams,
            fieldType,
            variant,
            showDivider = false,
            style,
          } = action || {};

          const localisedLabel = localisationParams
            ? t(tooltipLabel, localisationParams)
            : t(tooltipLabel);

          const localisedDisabledParamsTooltip = localisationParams
            ? t(disabledTooltip, localisationParams)
            : t(disabledTooltip);

          const localisedDisabledTooltip = isLocalised
            ? disabledTooltip
            : localisedDisabledParamsTooltip;

          return (
            <>
              <Tooltip
                placement="bottom"
                tooltip={disabled ? localisedDisabledTooltip : localisedLabel}
                overlayInnerStyle={styles.overlayInnerStyle}
                {...tooltipProps}
                showArrow={true}
              >
                <Button
                  key={key}
                  size="small"
                  variant={variant || "neutral"}
                  type="inline"
                  icon={icon}
                  dsVersion="2.0"
                  style={style}
                  onClick={() =>
                    _onClickAction({
                      key,
                      fieldType,
                      localisationParams,
                    })
                  }
                  disabled={disabled}
                  testId={`aiChatBody-${key}`}
                >
                  {localisationParams ? t(label, localisationParams) : t(label)}
                </Button>
              </Tooltip>

              {showDivider ? <div className={classes.divider} /> : null}
            </>
          );
        })}
      </div>
    );
  };

  // render attachments through theatre mode
  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const closePreviewModal = () => {
      setMediaViewerModalState(null);
    };

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  const handleRetryResponse = async ({ node }) => {
    if (!node) return;

    const parentMessageId = _.get(node, "parentMessage.id");
    const userMessageIndex = _.findIndex(messages, ({ node }) => {
      return node?.id === parentMessageId && node?.authorType === "user";
    });

    if (userMessageIndex === -1) return;

    const ids = _.compact(
      _.map(_.slice(messages, userMessageIndex + 1), message =>
        _.get(message, "node.id")
      )
    );

    setCurrentAIState(THINKING);

    await deleteAiMessages({
      messageIds: ids,
      conversationId: conversation?.id,
      shouldRefetch: false,
    });

    const userMessage = messages?.[userMessageIndex]?.node;

    const { content, attachments, metadata } = userMessage || {};
    const { learningTool } = metadata || {};

    if (!_.isEmpty(learningTool)) {
      const selectedTool = _.find(AI_TUTOR_LEARNING_TOOLS, {
        key: learningTool,
      });

      if (!_.isEmpty(selectedTool)) {
        setSelectedLearningTool(selectedTool);
      }
    }

    setIsAIGeneratingResponse(true);

    await onSendMessage?.({
      content,
      attachments,
      sendButtonParams: {
        createUserMessage: false,
        userMessageId: userMessage?.id,
      },
      learningTool,
      assistantMetadata: { isRetried: true },
    });

    setIsAIGeneratingResponse(false);
  };

  const ErrorMessage = ({
    node,
    containerClass,
    errorTextClass,
    iconVariant = "default",
    buttonType = "outlined",
    disableRetryButton = false,
    errorMsg = t("ai:response_interrupted_msg"),
  }) => {
    const isViewMode = mode === "view";

    return (
      <div className={classNames(classes.errorBlock, containerClass)}>
        <div className={classes.errorBlockContent}>
          <InformationOutlined variant={iconVariant} />
          <div
            className={classNames(
              "text-body",
              classes.errorBlockText,
              errorTextClass
            )}
          >
            {errorMsg}
          </div>
        </div>

        <Button
          dsVersion="2.0"
          type={buttonType}
          variant="neutral"
          size="small"
          icon={<RefreshArrowOutlined />}
          disabled={disableRetryButton || isViewMode}
          onClick={() => handleRetryResponse({ node })}
        >
          {t("common:retry")}
        </Button>
      </div>
    );
  };

  const renderMessages = () => {
    // Precompute the id of the last assistant message that contains a banner image block

    return (
      <div className={classes.messagesContainer} ref={messagesContainerRef}>
        {_.map(messages, ({ node }, index) => {
          const {
            id,
            content: _content,
            authorType,
            metadata,
            createdBy,
            votes,
            attachments,
            usageMetricsId,
            createdAt,
          } = node || {};

          const { profileImage } = createdBy || {};
          const formattedUserName = getFormattedUserName({
            user: createdBy,
            type: "staff",
          });

          const totalAttachments = _.size(attachments);

          switch (authorType) {
            case "user": {
              // filter empty messages that were created
              // only for storing tool use history
              const isContentEmpty = _.isEmpty(_.trim(_content));

              if (
                (isContentEmpty && totalAttachments === 0) ||
                metadata?.isMeta
              ) {
                return null;
              }

              const isLastUserMessage = lastUserMessageId === id;

              // as id changes due to optimistic response (NEW_ to actual id), for audio attachment
              // this causes audio player (wavesurfer) visualisation to re-render and flicker
              const audioAttachment = _.find(attachments, { type: "AUDIO" });
              const uniqueId = !_.isEmpty(audioAttachment)
                ? audioAttachment?.url
                : id;

              const attachmentIdsInValue = extractAttachmentIdsFromValue(
                _content
              );

              // Check if the message content contains markdown links
              const containsMarkdownLinks = /\[.*?\]\(https?:\/\/.*?\)/g.test(
                _content
              );

              const filteredAttachments = _.filter(
                attachments,
                ({ id, type }) => {
                  const isInEmbeddedContent = _.includes(
                    attachmentIdsInValue,
                    id
                  );
                  const isLinkOrVideoType = _.includes(["LINK", "VIDEO"], type);

                  if (isLinkOrVideoType && containsMarkdownLinks) {
                    return false;
                  }

                  return !isInEmbeddedContent;
                }
              );

              const actionsList =
                customUserActionsList?.(_content) ||
                getUserBlockActions(_content);

              return (
                <div
                  key={uniqueId}
                  className={classes.userMessageContainer}
                  ref={isLastUserMessage ? lastUserMessageRef : null}
                >
                  <div className={classes.userActionsContainer}>
                    {renderMediaCard(node, filteredAttachments)}

                    {!isContentEmpty ? (
                      <div
                        className={classNames(classes.userMessage, "text-body")}
                        id={`${id}_AI_MESSAGE`}
                      >
                        <MarkdownView
                          content={escapeMarkdownInUserMessage(_content)}
                          components={markdownComponents}
                          contentContainerClass={classes.markdownView}
                          enableHTMLrendering={!_.isEmpty(attachmentIdsInValue)}
                        />
                      </div>
                    ) : null}
                    <div className={classes.iconsContainer}>
                      {renderActions({
                        node,
                        actionsList,
                      })}

                      {createdAt ? (
                        <>
                          {!_.isEmpty(actionsList) ? (
                            <span className={classes.divider} />
                          ) : null}

                          <span className={"text-body-s"}>
                            {moment(createdAt).format("h:mm A")}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <Tooltip
                    tooltip={formattedUserName}
                    dsVersion="2.0"
                    align={{ offset: [-32, 0] }}
                  >
                    <div className={classes.userAvatarContainer}>
                      <Avatar
                        size="small"
                        shape="circle"
                        shade="light"
                        className={classes.userAvatar}
                        name={formattedUserName}
                        src={profileImage}
                        dsVersion="2.0"
                      />
                    </div>
                  </Tooltip>
                </div>
              );
            }

            case "assistant": {
              let blocks = null;
              let content = _content;

              const {
                userInputFlagged,
                model,
                temperature,
                intentUsageMetricsId,
                videoContext,
                isInterrupted = false,
              } = metadata || {};

              // do not translate AI generated content, as the HTML structure can be anything (which causes problem with google translate)
              // https://github.com/facebook/react/issues/11538#issuecomment-390386520

              const blockClass = classNames({
                ["notranslate"]: true,
                [classes.block]: true,
                [classes.textCritical]: userInputFlagged,
                ...(blockCustomClasses?.(node) || {}),
              });

              const blockWrapperClass = classNames({
                [classes.blockWrapper]: true,
                [classes.violationBlock]: userInputFlagged,
              });

              //  pass the util for custom parsing of content
              if (customContentParser) {
                content = customContentParser(content);
              }

              const isLastMessage = _.size(messages) - 1 === index;

              const shouldRenderErrorMessage =
                !_.isEmpty(_content) &&
                _.isEmpty(_.trim(content)) &&
                isLastMessage &&
                !isAIGeneratingResponse &&
                !getIsAIResponseLoading(currentAIState) &&
                !isInterrupted;

              if (shouldRenderErrorMessage) {
                return (
                  <ErrorMessage
                    node={node}
                    buttonType="fill"
                    iconVariant="critical"
                    errorMsg={t("ai:error_generating_response")}
                  />
                );
              }

              blocks = getBlocks({ content });

              // prepare actions list to show below the message block
              const defaultActionList = getAssistantBlockActions();
              const actionsList =
                customAIActionsList?.(node, index) || defaultActionList;

              // for audio only tutor, hide the assistant message box till the audio starts playing
              if (checkAssistantMessageVisibility) {
                const shouldShowMessage = checkAssistantMessageVisibility(
                  index
                );

                if (!shouldShowMessage) return null;
              }

              const timestamp = _.get(videoContext, "timestamp");

              const hasAttachmentBlock = _.some(blocks, {
                type: "ATTACHMENTS",
              });

              const showAttachments =
                !hasAttachmentBlock && totalAttachments > 0;

              const showActions =
                !userInputFlagged &&
                _.get(metadata, "showActions", true) &&
                (!isLastMessage ||
                  (isLastMessage && !getIsAIResponseLoading(currentAIState))) &&
                !(
                  hasAttachmentBlock &&
                  _.startsWith(id, "NEW_") &&
                  _.isEmpty(attachments)
                );

              const renderDebugMode = () => {
                if (!showActions) return null;

                const showAIFeedbackStrip =
                  showFeedbackStrip && (usageMetricsId || intentUsageMetricsId);

                return (
                  <div className={classes.actionsContainer}>
                    {renderActions({
                      node,
                      actionsList,
                      onClickAction,
                    })}

                    {showAIFeedbackStrip ? (
                      <AIFeedbackStrip
                        showWarningText={false}
                        containerStyle={styles.containerStyle}
                        defaultFeedbackStatus={votes.myVote}
                        usageMetricsId={usageMetricsId}
                        model={model}
                        temperature={temperature}
                        showLikeButton={showLikeDislikeButton}
                        showDislikeButton={showLikeDislikeButton}
                        intentUsageMetricsId={intentUsageMetricsId}
                        sourceFrom={sourceFrom}
                        showFeedbackStripV2={true}
                        miscInfo={
                          !_.isEmpty(_.get(metadata, "instructionsForTTSModel"))
                            ? `**TTS Model Instructions:** \n ${_.get(
                                metadata,
                                "instructionsForTTSModel"
                              )}`
                            : null
                        }
                        {...styles.aiFeedbackStripStyle}
                      />
                    ) : null}

                    {createdAt ? (
                      <>
                        {(!_.isEmpty(actionsList) || showAIFeedbackStrip) && (
                          <span className={classes.dividerWithoutMargin} />
                        )}
                        <span className={"text-body-s"}>
                          {moment(createdAt).format("h:mm A")}
                        </span>
                      </>
                    ) : null}
                  </div>
                );
              };

              return !_.isEmpty(blocks) ? (
                <React.Fragment key={id}>
                  <div className={classes.assistantMessage}>
                    <div className={classes.flexDirectionColumn}>
                      {customAssistantMessageRenderer ? (
                        <div className={classes.audioMsgContainer}>
                          {customAssistantMessageRenderer({ node, index })}
                          {showAttachments
                            ? renderMediaCard(node, attachments)
                            : null}
                          {renderDebugMode()}
                        </div>
                      ) : (
                        <div className={blockWrapperClass}>
                          <div className={blockClass}>
                            {/* video timestamp is specific to AI tutor */}
                            {timestamp ? (
                              <div className={classes.videoTimestampContainer}>
                                <VideoOutlined
                                  size="xxx-small"
                                  variant="subtle"
                                />
                                <span
                                  className={classNames(
                                    "text-body-s",
                                    classes.textDisabled
                                  )}
                                >
                                  {timestamp}
                                </span>
                              </div>
                            ) : null}

                            {renderBlocks({
                              blocks,
                              node,
                              currentAIState,
                              isLastMessage,
                            })}
                          </div>

                          {showAttachments
                            ? renderMediaCard(node, attachments)
                            : null}

                          {customAssistantTrailingMessageRenderer
                            ? customAssistantTrailingMessageRenderer(node)
                            : null}

                          {isInterrupted ? (
                            <ErrorMessage
                              node={node}
                              containerClass={
                                classes.interruptedResponseErrorBlock
                              }
                              disableRetryButton={!isLastMessage}
                              errorTextClass={classes.errorText}
                            />
                          ) : null}

                          {renderDebugMode()}
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              ) : null;
            }

            case "divider": {
              return (
                <div key={id} className={classes.dividerContainer}>
                  <div className={classes.dividerLine} />
                  <span
                    className={classNames(classes.dividerText, "text-body-s")}
                  >
                    {_content}
                  </span>
                  <div className={classes.dividerLine} />
                </div>
              );
            }
            case "interruptedResponse": {
              const isLastMessage = _.size(messages) - 1 === index;

              return (
                <ErrorMessage
                  node={node}
                  disableRetryButton={!isLastMessage}
                  errorTextClass={classes.errorText}
                  containerClass={classes.interruptedResponseErrorForAudio}
                />
              );
            }

            default:
              return renderCustomMessage?.({ message: node, index });
          }
        })}

        {/* render message loader while generating ai response */}
        {renderMessageLoader()}
      </div>
    );
  };

  const renderBodyContainerEnd = () => {
    if (!hasMessages) return null;
    return <div className={classes.scrollEnd} ref={scrollEndRef} />;
  };

  const hasMessages = !_.isEmpty(messages);

  const isAITutor = entitySubType === "ai_tutor";
  const DEFAULT_PROMPT_COUNT_TO_SHOW = isAITutor ? 6 : 4;
  const SINGLE_COLUMN_PROMPTS = isAITutor ? THREE : TWO;

  const promptCountToShow = showOnlySingleColumnPrompts
    ? SINGLE_COLUMN_PROMPTS
    : DEFAULT_PROMPT_COUNT_TO_SHOW;

  return (
    <div
      ref={scrollRef}
      className={classes.container}
      onScroll={onScrollBodyContainer}
    >
      {hasMessages ? renderMessages() : renderEmptyState({ promptCountToShow })}

      {renderMediaViewer()}

      {renderBodyContainerEnd()}

      {shouldRenderEmptySpace ? (
        <div className={classes.emptySpace} ref={emptySpaceRef} />
      ) : null}

      {/* Scroll to bottom button */}
      {hasMessages && showScrollToBottom && !isScrollingToBottom ? (
        <div className={classes.scrollToBottomButtonContainer}>
          <IconButton
            icon={<DownArrowOutlined />}
            variant={"neutral"}
            type={"outlined"}
            onClick={() => scrollToBottom({ isForced: true })}
            className={classes.scrollToBottomButton}
            size={"small"}
            dsVersion={"2.0"}
          />
        </div>
      ) : null}
    </div>
  );
};

const mapActionCreators = {
  openLinkInNewTab,
  setToastMsg,
  deleteAiMessages,
  updateAIConversation,
};

export default connect(null, mapActionCreators)(Body);
